import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, Loader2, RefreshCw, Settings } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { AitryxIngramCobrand } from './lockups';
import {
  IngramConfig,
  IngramConnectionTestResult,
  SyncResult,
  deleteIngramConfig,
  getIngramConfig,
  listEnrichments,
  saveIngramConfig,
  testIngramConnection,
  triggerDistributorSync,
} from '../../services/distributorService';
import { useNavigate } from 'react-router-dom';
import { CatalogBrowsePanel } from './CatalogBrowsePanel';
import { ConfigurationSection } from './ConfigurationSection';
import { EnrichmentReviewPanel } from './EnrichmentReviewPanel';
import { OverviewSection } from './OverviewSection';
import { SyncedProductsPanel } from './SyncedProductsPanel';
import { SyncHistoryPanel } from './SyncHistoryPanel';
import { DistributorShell, DistributorShellStatus } from '../DistributorsTab/DistributorShell';

/**
 * Ingram Micro Xvantage connector page. Tab-based shell — landing tab is
 * Overview when connected, Configuration when not. The header (logo +
 * title + badges + Configure / Run sync buttons) is sticky across tabs.
 *
 * Sub-tabs:
 *   • Overview        — KPI tiles + vendor coverage + enrichment funnel
 *   • Catalogue browse — live Ingram catalogue search + add to seed list
 *   • Enrichment review — Draft / Active / Rejected list with bulk actions
 *   • Seed SKUs       — manual SKU list (paste-add / pause / remove)
 *   • Sync history    — collapsible per-run timeline with price/stock deltas
 *   • Configuration   — credential form + connection test + disconnect
 */

const SECRET_PLACEHOLDER = '***configured***';

type TabKey = 'overview' | 'synced' | 'browse' | 'review' | 'history' | 'config';

interface TabDef {
  key: TabKey;
  label: string;
  /** Optional badge — currently used for the Draft count on Enrichment review. */
  badge?: number;
}

interface FormState {
  clientId: string;
  clientSecret: string;
  customerNumber: string;
  countryCode: string;
  senderId: string;
}

const emptyForm: FormState = {
  clientId: '',
  clientSecret: '',
  customerNumber: '',
  countryCode: 'AU',
  senderId: '',
};

export function IngramTab() {
  const [config, setConfig] = useState<IngramConfig | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<IngramConnectionTestResult | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [draftCount, setDraftCount] = useState<number | null>(null);
  const [tab, setTab] = useState<TabKey>('overview');
  // SKU set when the partner clicks Edit on a Browse pane row → review tab
  // pre-selects it. Cleared by the review panel after consuming.
  const [reviewDeeplinkSku, setReviewDeeplinkSku] = useState<string | null>(null);
  // Monotonic counter the parent bumps after Run sync to force the per-tab
  // panels (Synced products, Enrichment review) to re-fetch. The trigger
  // endpoint returns the moment messages are on the queue, but the worker
  // takes ~10–15s per SKU to actually commit a Draft enrichment row — so a
  // single bump on response is not enough. We follow up with a polling
  // loop (see runEnrichmentDrainPoll) that bumps on every tick until either
  // the expected number of new rows has appeared or a 90s budget elapses.
  const [refreshKey, setRefreshKey] = useState(0);
  const [enrichingCount, setEnrichingCount] = useState<number>(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cfg = await getIngramConfig();
      setConfig(cfg);
      setForm({
        clientId: cfg.clientId ?? '',
        clientSecret: cfg.clientSecret ?? '',
        customerNumber: cfg.customerNumber ?? '',
        countryCode: cfg.countryCode ?? 'AU',
        senderId: cfg.senderId ?? '',
      });
      // Default to Configuration when not yet connected so the form is
      // the first thing the partner sees on a fresh setup. Existing
      // selection is preserved on subsequent reloads.
      setTab((prev) => (cfg.connected ? prev : 'config'));
    } catch (err: any) {
      setError(err.message ?? 'Failed to load Ingram configuration');
      setTab('config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Lightweight Draft-count fetch for the tab badge — re-runs whenever the
  // user navigates back to Overview / Browse from another tab so the badge
  // stays roughly fresh without an explicit websocket.
  const refreshDraftCount = useCallback(async () => {
    if (!config?.connected) return;
    try {
      const list = await listEnrichments('ingram', 'Draft');
      setDraftCount(list.items?.length ?? 0);
    } catch {
      setDraftCount(null);
    }
  }, [config?.connected]);

  useEffect(() => {
    refreshDraftCount();
  }, [refreshDraftCount, tab, syncing]);

  const validate = (): string | null => {
    const needsClientId = !config?.connected || !form.clientId || form.clientId === SECRET_PLACEHOLDER;
    const needsClientSecret =
      !config?.connected || !form.clientSecret || form.clientSecret === SECRET_PLACEHOLDER;
    if (needsClientId && !form.clientId.trim()) return 'Client ID is required';
    if (needsClientSecret && !form.clientSecret.trim()) return 'Client secret is required';
    if (!form.customerNumber.trim()) return 'Customer number is required';
    if (!form.countryCode.trim() || form.countryCode.trim().length !== 2) {
      return 'Country code must be a 2-letter ISO code';
    }
    return null;
  };

  const handleTest = async () => {
    const msg = validate();
    if (msg) { setError(msg); return; }
    setTesting(true);
    setError(null);
    setSuccess(null);
    setTestResult(null);
    try {
      const result = await testIngramConnection({
        clientId: form.clientId,
        clientSecret: form.clientSecret,
        customerNumber: form.customerNumber.trim(),
        countryCode: form.countryCode.trim().toUpperCase(),
        senderId: form.senderId.trim() || undefined,
      });
      setTestResult(result);
    } catch (err: any) {
      setError(err.message ?? 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    const msg = validate();
    if (msg) { setError(msg); return; }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: {
        clientId?: string;
        clientSecret?: string;
        customerNumber: string;
        countryCode: string;
        senderId?: string;
      } = {
        customerNumber: form.customerNumber.trim(),
        countryCode: form.countryCode.trim().toUpperCase(),
        senderId: form.senderId.trim() || undefined,
      };
      if (form.clientId && form.clientId !== SECRET_PLACEHOLDER) payload.clientId = form.clientId;
      if (form.clientSecret && form.clientSecret !== SECRET_PLACEHOLDER) payload.clientSecret = form.clientSecret;
      await saveIngramConfig(payload);
      setSuccess('Ingram configuration saved.');
      await load();
    } catch (err: any) {
      setError(err.message ?? 'Failed to save Ingram configuration');
    } finally {
      setSaving(false);
    }
  };

  /**
   * After the trigger HTTP returns we know the producer has fanned out N
   * messages to the enrichment queue, but the worker hasn't finished writing
   * them yet. Poll listEnrichments every 3s and bump refreshKey on every
   * tick so SyncedProductsPanel + EnrichmentReviewPanel pick up rows the
   * moment they land in SQL. We stop early once we've seen `expected` new
   * rows for this distributor, and hard-cap at 90s to bound runaway loops
   * if the worker is wedged or the agent is throttled — the user can
   * always Refresh manually after that.
   */
  const runEnrichmentDrainPoll = useCallback(
    async (distributorId: string, baselineSkus: Set<string>, expected: number) => {
      const deadline = Date.now() + 90_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3_000));
        try {
          const list = await listEnrichments(distributorId);
          const newRows = (list.items ?? []).filter((it) => !baselineSkus.has(it.sku));
          // Bump even when newRows hasn't grown yet — Status transitions on
          // existing rows (e.g. Draft → Active during a partner approval
          // race) should also surface immediately.
          setRefreshKey((k) => k + 1);
          const remaining = Math.max(0, expected - newRows.length);
          setEnrichingCount(remaining);
          if (newRows.length >= expected) {
            setSuccess(
              expected === 1
                ? 'Enrichment complete: 1 new product is ready in Enrichment review.'
                : `Enrichment complete: ${expected} new products are ready in Enrichment review.`,
            );
            setEnrichingCount(0);
            return;
          }
        } catch {
          // Transient failure — keep polling. The next tick will retry.
        }
      }
      // Timed out — drop the indicator so it doesn't get stuck on screen.
      // The partner can re-check Enrichment review manually if rows are
      // still trickling in (the worker keeps draining whether we watch or
      // not — the timeout is a UI bound, not a backend bound).
      setEnrichingCount(0);
      setSuccess(
        'Sync completed. Enrichment is still running in the background — refresh in a minute or check Enrichment review.',
      );
    },
    [],
  );

  const handleRunSync = async () => {
    setSyncing(true);
    setError(null);
    setSuccess(null);
    setEnrichingCount(0);
    try {
      // Snapshot which SKUs already have an enrichment row so the post-sync
      // poll can detect newly-written rows without depending on a count
      // diff (which can be wrong if an admin removes an enrichment between
      // the baseline and the post-sync read).
      let baselineSkus = new Set<string>();
      try {
        const baseline = await listEnrichments('ingram');
        baselineSkus = new Set((baseline.items ?? []).map((it) => it.sku));
      } catch {
        // Non-fatal — we'll still bump refreshKey, just without a precise
        // "all new rows have landed" stop condition.
      }

      const result: SyncResult = await triggerDistributorSync('ingram');
      if (result.success) {
        const enqueued = result.enrichmentsEnqueued ?? 0;
        const baseMsg =
          `Sync completed: ${result.productsUpdated} products updated` +
          (result.productsFailed ? ` (${result.productsFailed} failed)` : '');
        if (enqueued > 0) {
          setSuccess(
            `${baseMsg}. Enriching ${enqueued} new ${enqueued === 1 ? 'product' : 'products'} in the background…`,
          );
          setEnrichingCount(enqueued);
        } else {
          setSuccess(baseMsg);
        }

        // Immediate bump catches existing-row updates (price/stock changes,
        // status flips) without waiting for the first poll tick.
        setRefreshKey((k) => k + 1);

        if (enqueued > 0) {
          // Fire the drain poll without awaiting — handleRunSync's "syncing"
          // state should release as soon as the trigger HTTP returns; the
          // background poll just keeps refreshing the panels independently.
          void runEnrichmentDrainPoll('ingram', baselineSkus, enqueued);
        }
      } else {
        setError(result.error || 'Sync failed');
      }
    } catch (err: any) {
      setError(err.message ?? 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Disconnect Ingram Micro? Sync will stop until you reconnect.')) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteIngramConfig();
      setSuccess('Ingram disconnected.');
      setForm(emptyForm);
      await load();
    } catch (err: any) {
      setError(err.message ?? 'Failed to disconnect');
    } finally {
      setSaving(false);
    }
  };

  const tabs: TabDef[] = useMemo(() => [
    { key: 'overview',  label: 'Overview' },
    { key: 'synced',    label: 'Live catalogue' },
    { key: 'browse',    label: 'Add products' },
    { key: 'review',    label: 'Enrichment review', badge: draftCount ?? undefined },
    { key: 'history',   label: 'Sync history' },
    { key: 'config',    label: 'Configuration' },
  ], [draftCount]);

  if (loading) {
    return (
      <div className="p-8 text-sm text-slate-500 inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading Ingram configuration…
      </div>
    );
  }

  const status: DistributorShellStatus = config?.connected
    ? {
        label: 'Connected',
        tone: 'connected',
        description: 'Catalogue watched live, syncs every 6 hours',
      }
    : {
        label: 'Not connected',
        tone: 'neutral',
        description: 'Configure your Xvantage credentials to start syncing',
      };

  const shellTabs = tabs.map((t) => ({
    ...t,
    disabled: !config?.connected && t.key !== 'config',
  }));

  const actions = (
    <>
      {!syncing && enrichingCount > 0 && (
        <div className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-amber-50 border border-amber-200">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-700" />
          <span className="text-[12.5px] text-amber-900">
            Enriching <strong className="font-semibold tabular-nums">{enrichingCount}</strong>
          </span>
        </div>
      )}
      {(draftCount ?? 0) > 0 && (
        <button
          type="button"
          onClick={() => setTab('review')}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-orange-50 border border-orange-200 hover:bg-orange-100 transition-colors"
          title="Open enrichment review"
        >
          <Bell className="h-3.5 w-3.5 text-orange-700" />
          <span className="text-[12.5px] text-orange-900">
            <strong className="font-semibold tabular-nums">{draftCount}</strong> in review
          </span>
        </button>
      )}
      <Button
        variant="outline"
        onClick={() => setTab('config')}
        disabled={saving || testing || syncing}
        className="inline-flex h-9 gap-1.5"
      >
        <Settings className="h-3.5 w-3.5" />
        Configure
      </Button>
      <Button
        onClick={handleRunSync}
        disabled={!config?.connected || saving || testing || syncing}
        className="inline-flex h-9 gap-1.5 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-600 text-white"
        title={config?.connected ? 'Trigger a manual sync now' : 'Configure the connection first'}
      >
        {syncing ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Syncing…
          </>
        ) : (
          <>
            <RefreshCw className="h-3.5 w-3.5" />
            Sync now
          </>
        )}
      </Button>
    </>
  );

  const banners = (error || success || testResult) ? (
    <>
      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {success}
        </div>
      )}
      {testResult && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${
            testResult.success
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-rose-200 bg-rose-50 text-rose-700'
          }`}
        >
          <div className="font-medium">
            {testResult.success ? 'Connection successful' : 'Connection failed'}
          </div>
          <div className="text-xs opacity-80">
            {testResult.success
              ? `Round-trip ${testResult.latencyMs} ms`
              : testResult.error || testResult.message}
          </div>
        </div>
      )}
    </>
  ) : null;

  return (
    <DistributorShell<TabKey>
      lockup={<AitryxIngramCobrand />}
      title="Ingram Micro Xvantage Connector"
      currentCrumb="Ingram Micro Xvantage"
      status={status}
      actions={actions}
      tabs={shellTabs}
      activeTab={tab}
      onTabChange={setTab}
      banners={banners}
    >
      {tab === 'overview' && config?.connected && (
        <OverviewSection distributorId="ingram" distributorLabel="Ingram" />
      )}
      {tab === 'browse' && config?.connected && (
        <CatalogBrowsePanel
          distributorId="ingram"
          distributorLabel="Ingram"
          onSwitchToReview={(sku) => {
            if (sku) setReviewDeeplinkSku(sku);
            setTab('review');
          }}
        />
      )}
      {tab === 'review' && config?.connected && (
        <EnrichmentReviewPanel
          distributorId="ingram"
          distributorLabel="Ingram"
          initialSelectedSku={reviewDeeplinkSku}
          onSkuConsumed={() => setReviewDeeplinkSku(null)}
          refreshKey={refreshKey}
        />
      )}
      {tab === 'synced' && config?.connected && (
        <SyncedProductsPanel
          distributorId="ingram"
          distributorLabel="Ingram"
          onSwitchToProductsPage={() => navigate('/marketplace?tab=products')}
          refreshKey={refreshKey}
        />
      )}
      {tab === 'history' && config?.connected && (
        <SyncHistoryPanel distributorId="ingram" distributorLabel="Ingram" />
      )}
      {tab === 'config' && (
        <div className="px-6 pb-10 max-w-3xl">
          <ConfigurationSection
            form={form}
            setForm={setForm}
            connected={!!config?.connected}
            lastTestedAt={config?.lastTestedAt ?? null}
            lastTestResult={config?.lastTestResult ?? null}
            saving={saving}
            testing={testing}
            syncing={syncing}
            onTest={handleTest}
            onSave={handleSave}
            onRunSync={handleRunSync}
            onDisconnect={handleDisconnect}
          />
        </div>
      )}
    </DistributorShell>
  );
}
